
import React, { useState, useRef, useEffect, useCallback, useContext } from "react";
import { Button, Form, Table, Modal } from "react-bootstrap";
import { FaEye, FaDownload, FaReceipt, FaUpload, FaEdit } from "react-icons/fa";
import Papa from "papaparse";
import { useNavigate, useLocation } from "react-router-dom";
import { saveAs } from "file-saver";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BASE_URL, updateInvoiceStatusNetworkForApproved } from "../../api/api";
import { IconButton } from "@mui/material";
import pdfimage from "../images/pdf_downlaod.png";
import { AuthContext } from "../../context/AuthContext";
import Swal from "sweetalert2";
import { statusColor } from "../../utils/statusColor";

function ApprovedBatchData() {
    const { user } = useContext(AuthContext);
    const isFinance = user?.role === "finance" || user?.role === "Finance";
    const isDisabled = user?.role === "NetworkSubAdmin"
    const navigate = useNavigate();
    const location = useLocation();
    const { batchData, batchNo, invoiceStatus } = location.state || {};
    const fileInputRef = useRef();
    const [invoices, setInvoices] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [isGSTApplied, setIsGSTApplied] = useState(true);
    const [apiData, setApiData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [fileType, setFileType] = useState("");
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingInvoice, setEditingInvoice] = useState(null);
    const [existingInvoice, setExistingInvoice] = useState({
        url: null,
        fileName: null,
        fileType: null,
    });
    const [invoiceNo, setInvoiceNo] = useState("");
    const [caseCount, setCaseCount] = useState("");
    const [invoiceDate, setInvoiceDate] = useState("");
    const [invoiceAmount, setInvoiceAmount] = useState("");
    const [amountError, setAmountError] = useState("");
    const [caseCountError, setCaseCountError] = useState("");
    const [hoveredRow, setHoveredRow] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({
        invoiceNo: false,
        invoiceDate: false,
        invoiceAmount: false,
        caseCount: false,
        invoiceFile: false,
        remarks: {},
    });
    const [editValues, setEditValues] = useState({
        serviceCharges: "",
        repairCharges: "",
        chargesInclGST: "",
        remarks: "",
        remarkFile: null,
    });

    const itemsPerPage = 10;
    const totalPages = Math.ceil(invoices.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentInvoices = invoices.slice(indexOfFirstItem, indexOfLastItem);
    const selectedAAno = invoices.filter((invoice) => invoice.isChecked);

    // Calculate totals based on selected invoices only
    const totalRepairCharges = selectedAAno.reduce((total, invoice) => {
        const cleanedAmount = invoice.repairCharges?.toString().replace(/,/g, "");
        const amount = parseFloat(cleanedAmount);
        return total + (isNaN(amount) ? 0 : amount);
    }, 0);

    const totalServiceCharges = selectedAAno.reduce((total, invoice) => {
        const cleanedAmount = invoice.serviceCharges?.toString().replace(/,/g, "");
        const amount = parseFloat(cleanedAmount);
        return total + (isNaN(amount) ? 0 : amount);
    }, 0);

    const gstAmount = isGSTApplied
        ? (totalServiceCharges * 0.18).toFixed(2)
        : (0).toFixed(2);
    const totalservicechargewithGST = (
        totalServiceCharges + parseFloat(gstAmount)
    ).toFixed(2);

    const finalAmount = (
        totalRepairCharges +
        totalServiceCharges +
        parseFloat(gstAmount)
    ).toFixed(2);

    useEffect(() => {
        if (!batchData) {
            navigate("/invoice");
            return;
        }
        setCaseCount(batchData.caseCount || "");

        if (!batchData.gst || batchData.gst === "0.00") {
            setIsGSTApplied(false);
        } else {
            setIsGSTApplied(true);
        }
        setInvoiceNo(batchData.invoiceNo || "");
        setInvoiceDate(batchData.invoiceDate || "");
        setInvoiceAmount(batchData.invoiceAmount || "");
        if (batchData.invoice) {
            const fileName = batchData.invoice.split("/").pop() || "invoice.pdf";
            const fileType = fileName.split(".").pop()?.toLowerCase() || "";
            setExistingInvoice({
                url: batchData.invoice,
                fileName: fileName,
                fileType: fileType,
            });
            setFileType(fileType);
        }
    }, [batchData, navigate]);

    useEffect(() => {
        if (!batchData) return;
        setLoading(true);
        if (batchData.aaNo) {
            const aaNumbers = batchData.aaNo.split(",").map((val) => val.trim());
            Promise.all(
                aaNumbers.map((aaNo) =>
                    fetch(`${BASE_URL}/GetGadgetCaseDetailsByAA?aaNumbers=${aaNo}`)
                        .then((response) => {
                            if (!response.ok) {
                                console.error(
                                    `HTTP error for aaNo ${aaNo}: ${response.status}`
                                );
                                return { status: false, aA_Number: aaNo, dataItems: [] };
                            }
                            return response.json();
                        })
                        .then((responseData) => {
                            if (!responseData || responseData.status === false) {
                                return { status: false, aA_Number: aaNo, dataItems: [] };
                            }
                            return {
                                status: responseData.status,
                                aA_Number: aaNo,
                                dataItems: Array.isArray(responseData.dataItems)
                                    ? responseData.dataItems
                                    : [],
                            };
                        })
                        .catch((error) => {
                            console.error(`Error fetching API data for ${aaNo}:`, error);
                            return { status: false, aA_Number: aaNo, dataItems: [] };
                        })
                )
            )
                .then((results) => {
                    setApiData(results.filter((item) => item));
                })
                .catch((error) => {
                    console.error("Error in Promise.all:", error);
                    setApiData([]);
                })
                .finally(() => setLoading(false));
        } else {
            setLoading(false);
        }
    }, [batchData]);

    useEffect(() => {
        if (!batchData) return;
        const splitData = (key) =>
            batchData[key]
                ?.split(",")
                .map((val) => (val === "NULL" ? "-" : val.trim())) || [];
        const remainAAArray = splitData("remainAA");
        const utrArray = splitData("utr");
        const utrMap = {};
        remainAAArray.forEach((aa, i) => {
            if (aa && aa !== "-") utrMap[aa] = utrArray[i] || "-";
        });
        const invoicesArray = splitData("aaNo").map((_, i) => ({
            aA_Number: splitData("aaNo")[i],
            imeiNumber: splitData("imeiNo")[i],
            creationDate: batchData.creationDate || "",
            closureDate: batchData.closureDate || "",
            customerName: splitData("customerName")[i],
            serviceType: splitData("serviceType")[i],
            brand: splitData("brand")[i],
            makeModel: splitData("makeModel")[i],
            repairCharges: splitData("repairCharges")[i]
                ? parseFloat(splitData("repairCharges")[i]).toFixed(2)
                : "0.00",
            serviceCharges: splitData("serviceCharges")[i]
                ? parseFloat(splitData("serviceCharges")[i]).toFixed(2)
                : "0.00",
            chargesInclGST: splitData("chargesInclGST")[i]
                ? parseFloat(splitData("chargesInclGST")[i]).toFixed(2)
                : "0.00",
            total: splitData("total")[i]
                ? parseFloat(splitData("total")[i]).toFixed(2)
                : "0.00",
            invoiceStatus: batchData.invoiceStatus || "-",
            networkAndFinanceStatus: splitData("networkAndFinanceStatus")[i] || "-",
            sellingPartner: splitData("sellingPartner")[i],
            batchNo: batchData.batchNo || "",
            uniqueApproval: batchData.uniqueApproval || "",
            isChecked: true,
            remarks: splitData("remarks")[i] || "",
            remarkFile: batchData.remarkFile || null,
            utrNo: utrMap[splitData("aaNo")[i]] || "-",
        }));
        setInvoices(invoicesArray);
    }, [batchData]);

    const normalize = (value) =>
        (value ? value.toString().trim() : "").toLowerCase();

    const getRowClassName = useCallback(
        (invoice) => {
            if (!Array.isArray(apiData) || apiData.length === 0) return "row-red";
            const matchedData = apiData.find(
                (item) => item.aA_Number === invoice.aA_Number
            );
            if (!matchedData || matchedData.status === false) return "row-red";
            if (
                matchedData.status === true &&
                Array.isArray(matchedData.dataItems) &&
                matchedData.dataItems.length > 0
            ) {
                const dataItem = matchedData.dataItems[0];
                if (
                    normalize(dataItem.serviceType) !== normalize(invoice.serviceType) ||
                    normalize(dataItem.repairCharges) !==
                    normalize(invoice.repairCharges) ||
                    normalize(dataItem.serviceCharges) !==
                    normalize(invoice.serviceCharges) ||
                    normalize(dataItem.total) !== normalize(invoice.total) ||
                    normalize(dataItem.imeiNumber) !== normalize(invoice.imeiNumber)
                    //  ||
                    // normalize(dataItem.sellingPartner) !==
                    // normalize(invoice.sellingPartner)
                ) {
                    return "row-yellow";
                }
                return "row-green";
            }
            return "row-red";
        },
        [apiData]
    );

    const getDifferencesData = (invoice) => {
        const matchedData = apiData.find(
            (item) => item.aA_Number === invoice.aA_Number
        );
        if (!matchedData || matchedData.status === false)
            return [{ field: "AA Number", table: invoice.aA_Number, api: "" }];
        const dataItem = matchedData.dataItems[0] || {};
        const differences = [];
        if (normalize(dataItem.serviceType) !== normalize(invoice.serviceType)) {
            differences.push({
                field: "Service Type",
                table: invoice.serviceType || "",
                api: dataItem.serviceType || "",
            });
        }
        if (normalize(dataItem.imeiNumber) !== normalize(invoice.imeiNumber)) {
            differences.push({
                field: "IMEI Number",
                table: invoice.imeiNumber || "",
                api: dataItem.imeiNumber || "",
            });
        }
        // if (
        //   normalize(dataItem.sellingPartner) !== normalize(invoice.sellingPartner)
        // ) {
        //   differences.push({
        //     field: "Selling Partner",
        //     table: invoice.sellingPartner || "",
        //     api: dataItem.sellingPartner || "",
        //   });
        // }
        if (
            normalize(dataItem.repairCharges) !== normalize(invoice.repairCharges)
        ) {
            differences.push({
                field: "Repair Charges",
                table: invoice.repairCharges || "",
                api: dataItem.repairCharges || "",
            });
        }
        if (
            normalize(dataItem.serviceCharges) !== normalize(invoice.serviceCharges)
        ) {
            differences.push({
                field: "Service Charges",
                table: invoice.serviceCharges || "",
                api: dataItem.serviceCharges || "",
            });
        }
        // if (
        //   normalize(dataItem.chargesInclGST) !== normalize(invoice.chargesInclGST)
        // ) {
        //   differences.push({
        //     field: "GST Charges",
        //     table: invoice.chargesInclGST || "",
        //     api: dataItem.chargesInclGST || "",
        //   });
        // }
        if (normalize(dataItem.total) !== normalize(invoice.total)) {
            differences.push({
                field: "Total Amount",
                table: invoice.total || "",
                api: dataItem.total || "",
            });
        }
        return differences.length > 0 ? differences : [];
    };
    const downloadCSV = () => {
        const headers = [
            "AANumber",
            "IMEINumber",
            // "CreationDate",
            // "ClosureDate",
            "CustomerName",
            "Brand",
            "MakeModel",
            "RepairCharges",
            "ServiceCharges (Pre GST)",
            "Total",
            "InvoiceStatus",
            // "Remarks",
            // "Remark File",
        ];

        const today = new Date().toLocaleDateString("en-GB");

        const data = selectedAAno.map((invoice) => {
            const isPaidStatus = invoice.invoiceStatus?.trim().toLowerCase() === "paid" || 
                                 invoice.invoiceStatus?.trim().toLowerCase() === "payment done" || 
                                 (invoice.utrNo && invoice.utrNo.trim() !== "-" && invoice.utrNo.trim() !== "");
            return {
                aaNumber: invoice.aA_Number || "",
                imeiNumber: invoice.imeiNumber ? `="${invoice.imeiNumber}"` : "",
                // ClosureDate: (invoice.closureDate || "").split(" ")[0],
                // CreationDate: (invoice.creationDate || "").split(" ")[0],
                customerName: invoice.customerName || "",
                brand: invoice.brand || "",
                makeModel: invoice.makeModel || "",
                repairCharges: invoice.repairCharges || "0.00",
                serviceCharges: invoice.serviceCharges || "0.00",
                total: invoice.total || "0.00",
                invoiceStatus: isPaidStatus ? "Paid" : "Processed",
            };
        });

        const csv = Papa.unparse({
            fields: headers,
            data: data.map(row => Object.values(row)),
        }, {
            quotes: true,
            quoteChar: '"',
            delimiter: ',',
            newline: '\r\n'
        });
        const vendorName =
            selectedAAno?.[0]?.sellingPartner?.replace(/\s+/g, "_") || "invoiceList.csv";

        const fileName = `${vendorName}_list.csv`;

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        saveAs(blob, fileName);
    };
    const handleDownloadInvoice = () => {
        const invoiceUrl = batchData?.invoice;
        if (!invoiceUrl) {
            toast.error("No invoice URL available for download.");
            return;
        }
        const fileName = invoiceUrl.split("/").pop() || "invoice.pdf";
        const link = document.createElement("a");
        link.href = invoiceUrl;
        link.download = fileName;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const getAllowedAANumbers = () => {
        const aaNumbers = currentInvoices
            .map(invoice => invoice.uniqueApproval)
            .filter(Boolean); // removes null/undefined/empty strings

        if (aaNumbers.length === 0) return null;

        return aaNumbers.flatMap(aaString =>
            aaString.split(",").map(aa => aa.trim())
        );
    };

    const isRowDisabled = (invoice) => {
        const allowedAA = getAllowedAANumbers();

        // If no AA numbers → all rows active
        if (allowedAA === null) return false;

        // Otherwise → only allow rows in the allowed list
        return !allowedAA.includes(invoice.aA_Number);
    };
    const getDisabledAANumbers = () => {
        return invoices
            .filter(inv => isRowDisabled(inv)) // only disabled rows
            .map(inv => inv.aA_Number || "")
            .filter(Boolean)
            .join(",");
    };
    function parseInvoiceDate(dateStr) {
        if (!dateStr) return new Date();

        const parsed = Date.parse(dateStr);

        if (!isNaN(parsed)) {
            return new Date(parsed);
        }

        const cleaned = dateStr.replace("AM", " AM").replace("PM", " PM");
        const dateObj = new Date(cleaned);

        if (!isNaN(dateObj.getTime())) {
            return dateObj;
        }

        throw new Error("Invalid date format: " + dateStr);
    }
    function formatDate(input) {
        if (!input) return "-";
        const s = String(input).trim().replace(/\s+/g, " "); // collapse double spaces

        // Excel serial number (possibly with fraction)
        if (/^\d+(\.\d+)?$/.test(s)) {
            const serial = parseFloat(s);
            const ms = (serial - 25569) * 86400 * 1000;
            const d = new Date(ms);
            return isNaN(d) ? "-" : ddmmyyyy(d.getDate(), d.getMonth() + 1, d.getFullYear());
        }

        let m;

        // 1) ISO-like: YYYY-MM-DD [HH:mm[:ss]]
        m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T]\d{2}:\d{2}(?::\d{2})?)?$/);
        if (m) return ddmmyyyy(Number(m[3]), Number(m[2]), Number(m[1]));

        // 2) DMY: DD-MM-YYYY [HH:mm[:ss]]
        m = s.match(/^(\d{2})-(\d{2})-(\d{4})(?:[ T]\d{2}:\d{2}(?::\d{2})?)?$/);
        if (m) return ddmmyyyy(Number(m[1]), Number(m[2]), Number(m[3]));

        // 3) Month-name: "Aug 21 2025" or "Aug 21 2025 8:01AM"
        m = s.match(/^([A-Za-z]{3}) (\d{1,2}) (\d{4})(?: (\d{1,2})(?::(\d{2}))?(AM|PM))?$/i);
        if (m) {
            const monthMap = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 };
            const month = monthMap[m[1].toLowerCase()];
            const day = Number(m[2]);
            const year = Number(m[3]);
            if (month && day && year) return ddmmyyyy(day, month, year);
        }

        // 4) Last resort: try native Date (replace first space with 'T' to help)
        const nd = new Date(s.replace(" ", "T"));
        if (!isNaN(nd)) return ddmmyyyy(nd.getDate(), nd.getMonth() + 1, nd.getFullYear());

        return "-";

        function ddmmyyyy(d, m, y) {
            const dd = String(d).padStart(2, "0");
            const mm = String(m).padStart(2, "0");
            return `${dd}/${mm}/${y}`;
        }
    }

    const getFileNameFromUrl = (url) => {
        const params = new URLSearchParams(url.split("?")[1]);
        return decodeURIComponent(params.get("fileName"));
    };

    const isPdf = (url) => {
        return url.toLowerCase().includes(".pdf");
    };
    const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const handlePreview = (url) => {
        if (!isPdf(url)) {
            setImagePreviewUrl(url);
            setShowModal(true);
        } else {
            window.open(url, "_blank");
        }
    };
    const handleLOGSremarks = (aa_number) => {
        navigate(`/Remarks&Logs/${aa_number}`)
    }
    return (
        <>
            <style>
                {`
          .row-red { background-color: #ffe6e6; color: #d32f2f; }
          .row-yellow { background-color: #fff9c4; }
          .row-green { background-color: #e8f5e9; }
          .approval-page-container { padding: 20px; max-width: 1212px; margin: 0 auto; }
          .batch_popup_upload {
            background-color: #8000d7; color: white; border: none;
            padding: 10px 16px; border-radius: 8px; font-weight: 500;
            font-size: 14px; display: flex; align-items: center; cursor: pointer;
          }
          .batch_popup_upload:hover { background-color: #6b00b8; }
          .table-container { overflow-x: auto; overflow-y: auto; max-height: 400px; width: 100%; }
          .network_table { min-width: 1200px; }
          .network_table th, .network_table td { padding: 8px; vertical-align: middle; white-space: nowrap; }
          .vendore_invoice_status { white-space: nowrap; }
        `}
            </style>
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
            />
            {showModal && (
                <div
                    className="custom-modal"
                    onClick={() => setShowModal(false)} // closes modal when clicked anywhere
                >
                    <span className="close">&times;</span>
                    <img src={imagePreviewUrl} alt="Preview" className="modal-content" />
                </div>
            )}

            <div className="approval-page-container" style={user?.role === "finance" || user?.role === "Finance" ? { maxWidth: "100%", paddingLeft: "30px", paddingRight: "30px" } : {}}>
                <div className="mt-4">
                    <div className="netwrok_table_main_content">
                        <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                            <div className="d-flex flex-wrap gap-4 align-items-center">
                                {batchData?.batchNo && (
                                    <div className="fw-semibold">
                                        <span>Batch No: </span>
                                        <span className="text-primary">{batchData.batchNo}</span>
                                    </div>
                                )}
                                {batchData?.vendorName && (
                                    <div className="fw-semibold">
                                        <span>Vendor: </span>
                                        <span className="text-success">{batchData.vendorName}</span>
                                    </div>
                                )}
                            </div>
                            <div className="d-flex gap-2">
                                {existingInvoice.url && (
                                    <button
                                        className="batch_popup_upload"
                                        onClick={handleDownloadInvoice}
                                    >
                                        <FaDownload />{" "}
                                        <span className="ms-2">Download Invoice</span>
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="d-flex justify-content-end text-end upload_file_ext_name mb-3">
                            {existingInvoice.url && !uploadedFile && (
                                <div style={{ fontWeight: "bold", color: "green", display: "flex", alignItems: "center", gap: "10px" }}>
                                    {/* <span>{getFileNameFromUrl(existingInvoice.url)}</span> */}
                                    <span
                                        onClick={() => window.open(existingInvoice.url, "_blank")}
                                        style={{ cursor: "pointer" }}>{getFileNameFromUrl(existingInvoice.url)}</span>
                                    {isPdf(existingInvoice.url) ? (
                                        <img src={pdfimage} alt="PDF" style={{ width: "24px", height: "24px" }} />
                                    ) : (
                                        <img src={existingInvoice.url} alt="Preview" style={{ width: "24px", height: "24px" }} />
                                    )}
                                </div>
                            )}

                            {fieldErrors.invoiceFile && (
                                <div className="text-danger mt-1" style={{ fontSize: "14px" }}>
                                    Please upload an invoice file.
                                </div>
                            )}
                        </div>

                        <div className="table-container mt-3">
                            <Table className="bg-white text-center border-0 network_table">
                                <thead
                                    style={{
                                        backgroundColor: "#EEF4FF",
                                        position: "sticky",
                                        top: 0,
                                        zIndex: 1,
                                    }}
                                >
                                    <tr className="text-dark fw-semibold table_th_border">

                                        {!isFinance && <th>View</th>}
                                        {!isFinance && <th>Remarks</th>}
                                        <th style={{ whiteSpace: "nowrap" }}>AA No</th>
                                        <th style={{ whiteSpace: "nowrap" }}>IMEI No</th>
                                        {/* <th style={{ whiteSpace: "nowrap" }}>Creation Date</th>
                                        <th style={{ whiteSpace: "nowrap" }}>Closure Date</th> */}
                                        <th style={{ whiteSpace: "nowrap" }}>Customer Name</th>
                                        <th style={{ whiteSpace: "nowrap" }}>Service Type</th>
                                        <th style={{ whiteSpace: "nowrap" }}>Brand</th>
                                        <th style={{ whiteSpace: "nowrap" }}>Make Model</th>
                                        <th style={{ whiteSpace: "nowrap" }}>Repair Charges</th>
                                        <th style={{ whiteSpace: "nowrap" }}>Service Charges(Pre GST)</th>
                                        <th style={{ whiteSpace: "nowrap" }}>Total</th>
                                        <th style={{ whiteSpace: "nowrap" }}>Invoice Status</th>
                                        <th style={{ whiteSpace: "nowrap" }}>UTR No</th>
                                        <th style={{ whiteSpace: "nowrap" }}>Mismatched Data</th>
                                        <th style={{ whiteSpace: "nowrap" }}>Remarks</th>
                                        <th style={{ whiteSpace: "nowrap" }}>Remarks File</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading && (
                                        <tr>
                                            <td colSpan={isFinance ? 17 : 19} className="text-center py-3">
                                                Validating data...
                                            </td>
                                        </tr>
                                    )}
                                    {!loading &&
                                        currentInvoices.map((invoice, index) => {
                                            const disabled = isRowDisabled(invoice);
                                            const isPaidStatus = invoice.invoiceStatus?.trim().toLowerCase() === "paid" || 
                                                                 invoice.invoiceStatus?.trim().toLowerCase() === "payment done" || 
                                                                 (invoice.utrNo && invoice.utrNo.trim() !== "-" && invoice.utrNo.trim() !== "");

                                            return (
                                                <tr
                                                    key={
                                                        invoice.aA_Number || `${invoice.aA_Number}-${index}`
                                                    }
                                                    className={`text-center border-bottom network_td_item ${getRowClassName(invoice)} ${isRowDisabled(invoice) ? 'table-row-disabled' : ''}`}
                                                    style={{
                                                        opacity: isRowDisabled(invoice) ? 0.6 : 1,
                                                        backgroundColor: isRowDisabled(invoice) ? '#f2f2f2' : 'transparent',
                                                        pointerEvents: isRowDisabled(invoice) ? 'none' : 'auto'
                                                    }}
                                                    aria-label={
                                                        getRowClassName(invoice).replace("row-", "") + " row"
                                                    }
                                                >
                                                    {!isFinance && (
                                                        <td className="align-middle">
                                                            <IconButton
                                                                onClick={() =>
                                                                    navigate("/invoice-template", {
                                                                        state: {
                                                                            aaNumber: invoice.aA_Number,
                                                                            invoiceData: invoice,
                                                                        },
                                                                    })
                                                                }
                                                                disabled={loading || isDisabled}
                                                            >
                                                                <FaEye
                                                                    size={20}
                                                                    className="text-purple review_fa_eye"
                                                                    style={{ cursor: "pointer" }}
                                                                />
                                                            </IconButton>
                                                        </td>
                                                    )}
                                                    {!isFinance && (
                                                        <td className="border-start align-middle">
                                                            <FaReceipt
                                                                size={20}
                                                                className="text-purple-600 review_fa_eye"
                                                                style={{ cursor: "pointer" }}
                                                                onClick={() => handleLOGSremarks(invoice.aA_Number)}
                                                            />
                                                        </td>
                                                    )}
                                                    <td className="align-middle">
                                                        {invoice.aA_Number || "-"}
                                                    </td>
                                                    <td className="align-middle">
                                                        {invoice.imeiNumber || "-"}
                                                    </td>
                                                    {/* <td className="align-middle">
                                                        {invoice.creationDate
                                                            ? formatDate(invoice.creationDate)
                                                            : "-"}
                                                    </td>
                                                    <td className="align-middle">
                                                        {invoice.closureDate
                                                            ? formatDate(invoice.closureDate)
                                                            : "-"}
                                                    </td> */}
                                                    <td className="align-middle">
                                                        {invoice.customerName || "-"}
                                                    </td>
                                                    <td className="align-middle">
                                                        {invoice.serviceType || "-"}
                                                    </td>
                                                    <td className="align-middle">{invoice.brand || "-"}</td>
                                                    <td className="align-middle">
                                                        {invoice.makeModel || "-"}
                                                    </td>
                                                    <td className="align-middle">
                                                        {invoice.repairCharges || "0.00"}
                                                    </td>
                                                    <td className="align-middle">
                                                        {invoice.serviceCharges || "0.00"}
                                                    </td>
                                                    <td className="align-middle">
                                                        {invoice.total || "0.00"}
                                                    </td>
                                                    <td className="align-middle">
                                                        <span className="vendore_invoice_status px-3 py-1 rounded-pill" style={{ backgroundColor: statusColor[isPaidStatus ? "Paid" : "Processed"]?.backgroundColor || "#F3F4F6", color: statusColor[isPaidStatus ? "Paid" : "Processed"]?.color || "#6B7280", border: `1px solid ${statusColor[isPaidStatus ? "Paid" : "Processed"]?.color || "#6B7280"}`, fontSize: "13px", fontWeight: "600", whiteSpace: "nowrap" }}>
                                                            {isPaidStatus ? "Paid" : "Processed"}
                                                        </span>
                                                    </td>
                                                    <td className="align-middle">
                                                        {invoice.utrNo || "-"}
                                                    </td>
                                                    <td
                                                        className="align-middle position-relative"
                                                        onMouseEnter={() => setHoveredRow(index)}
                                                        onMouseLeave={() => setHoveredRow(null)}
                                                    >
                                                        {getDifferencesData(invoice).length > 0
                                                            ? "Mismatch"
                                                            : "Valid"}
                                                        {hoveredRow === index &&
                                                            getDifferencesData(invoice).length > 0 && (
                                                                <div
                                                                    style={{
                                                                        position: "absolute",
                                                                        top: "100%",
                                                                        left: "-18%",
                                                                        transform: "translateX(-50%)",
                                                                        zIndex: 1000,
                                                                        backgroundColor: "#fff",
                                                                        border: "1px solid #ddd",
                                                                        padding: "10px",
                                                                        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                                                                        minWidth: "300px",
                                                                        whiteSpace: "nowrap",
                                                                    }}
                                                                >
                                                                    <table className="table table-sm table-bordered mb-0">
                                                                        <thead>
                                                                            <tr>
                                                                                <th>Field</th>
                                                                                <th>Excel Data</th>
                                                                                <th>System Data</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {getDifferencesData(invoice).map(
                                                                                (diff, i) => (
                                                                                    <tr key={i}>
                                                                                        <td>{diff.field}</td>
                                                                                        <td>{diff.table}</td>
                                                                                        <td>{diff.api}</td>
                                                                                    </tr>
                                                                                )
                                                                            )}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            )}
                                                    </td>
                                                    <td className="align-middle">
                                                        {invoice.remarks || "no remarks"}
                                                    </td>
                                                    <td className="align-middle border-end">
                                                        {invoice.remarkFile ? (
                                                            <a
                                                                href={invoice.remarkFile}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{
                                                                    color: "#8000d7",
                                                                    textDecoration: "underline",
                                                                }}
                                                            >
                                                                View File
                                                            </a>
                                                        ) : (
                                                            "-"
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                </tbody>
                            </Table>
                        </div>
                        <div className="d-flex justify-content-between align-items-center pagination-container mt-3">
                            <button
                                className="btn btn-outline-primary"
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            <span className="page-info">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                className="btn btn-outline-primary"
                                onClick={() =>
                                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                                }
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </button>
                        </div>
                        <div className="row justify-content-between align-items-start mt-2">
                            {/* Left Column */}
                            <div className="col-6">
                                {/* GST Toggle */}
                                <div className="border rounded p-2 mt-4">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="fw-bold ms-2">
                                            <span>GST</span>
                                        </div>
                                        <div className="d-flex gap-3">
                                            <label className="registration_kc_radio">
                                                <input
                                                    type="radio"
                                                    name="gstApplied"
                                                    checked={isGSTApplied === true}
                                                    onChange={() => setIsGSTApplied(true)}
                                                    disabled={true}
                                                />
                                                <span className="registration_applied_yes">Yes</span>
                                            </label>
                                            <span className="mx-1">/</span>
                                            <label className="registration_kc_radio">
                                                <input
                                                    type="radio"
                                                    name="gstApplied"
                                                    checked={isGSTApplied === false}
                                                    onChange={() => setIsGSTApplied(false)}
                                                    disabled={true}
                                                />
                                                <span className="registration_applied_yes">No</span>
                                            </label>
                                        </div>

                                    </div>
                                </div>

                                {/* Selected Services */}
                                <div
                                    style={{
                                        backgroundColor: "#eef4ff",
                                        marginTop: "8px",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        gap: "8px",
                                        padding: "16px",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    <span style={{ fontWeight: 600, color: "gray" }}>
                                        Total no of selected invoices:
                                    </span>
                                    <span style={{ fontWeight: "bold", color: "black" }}>
                                        {selectedAAno.length}
                                    </span>
                                </div>

                                {/* Amount Summary */}
                                <div
                                    className="batch_popup_gross_ammount mt-2 w-100"
                                    style={{ backgroundColor: "#eef4ff" }}
                                >
                                    <div className="d-flex justify-content-between batch_popup_amount">
                                        <div className="fw-bold batch_gross">Total Repair Charges</div>
                                        <div className="text-end batch_amount_to_fix">
                                            ₹ {parseFloat(totalRepairCharges).toFixed(2)}
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-between batch_popup_amount">
                                        <div className="fw-bold batch_gross">Total Service Charges</div>
                                        <div className="d-flex align-items-center">
                                            <div className="batch_amount_to_fix">
                                                ₹ {totalservicechargewithGST} ({isGSTApplied ? "incl GST 18%" : "excl GST"})
                                            </div>
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-between batch_popup_amount">
                                        <div className="fw-bold batch_gross">Total Amount</div>
                                        <div className="batch_amount_to_fix" style={{ fontSize: "14px" }}>
                                            <b>₹ {finalAmount}</b>
                                        </div>
                                    </div>

                                    <div className="mt-2 d-flex justify-content-end">
                                        <span className="fw-semibold text-secondary" style={{ fontSize: "13px" }}>
                                            {isGSTApplied
                                                ? `GST Amount Added: ₹${gstAmount}`
                                                : `No GST Applied (₹${gstAmount})`}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column (Form) */}
                            <div className="col-6" style={{ backgroundColor: "#eef4ff", marginTop: "1rem" }}>
                                <form className="mt-4 invoice_form">
                                    <div className="row align-items-center">
                                        {/* Case Count */}
                                        <div className="d-flex align-items-center mb-3">
                                            <label className="fw-semibold me-2" style={{ width: "200px" }}>Case Count</label>
                                            <input
                                                type="text"
                                                className={`form-control border-dark ${(uploadedFile || !existingInvoice.url) &&
                                                    (fieldErrors.caseCount || caseCountError) ? "is-invalid" : ""}`}
                                                placeholder="Case Count"
                                                value={caseCount}
                                                disabled={true}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setCaseCount(value);
                                                    if (uploadedFile || !existingInvoice.url) {
                                                        const parsedValue = parseInt(value);
                                                        setFieldErrors((prev) => ({
                                                            ...prev,
                                                            caseCount:
                                                                !value.trim() ||
                                                                isNaN(parsedValue) ||
                                                                parsedValue <= 0,
                                                        }));
                                                        if (
                                                            value.trim() &&
                                                            !isNaN(parsedValue) &&
                                                            parsedValue > 0
                                                        ) {
                                                            if (parsedValue !== selectedAAno.length) {
                                                                setCaseCountError("Case Count must match the total number of selected invoices.");
                                                            } else {
                                                                setCaseCountError("");
                                                            }
                                                        } else {
                                                            setCaseCountError("");
                                                        }
                                                    }
                                                }}
                                            />
                                        </div>
                                        {(uploadedFile || !existingInvoice.url) && fieldErrors.caseCount && (
                                            <div className="text-danger mt-1" style={{ fontSize: "14px" }}>
                                                Case Count must be a valid positive number.
                                            </div>
                                        )}
                                        {(uploadedFile || !existingInvoice.url) && caseCountError && (
                                            <div className="text-danger mt-1" style={{ fontSize: "14px" }}>
                                                {caseCountError}
                                            </div>
                                        )}

                                        {/* Invoice No */}
                                        <div className="d-flex align-items-center mb-3">
                                            <label className="fw-semibold me-2" style={{ width: "200px" }}>Invoice No</label>
                                            <input
                                                type="text"
                                                className={`form-control border-dark ${(uploadedFile || !existingInvoice.url) &&
                                                    fieldErrors.invoiceNo ? "is-invalid" : ""}`}
                                                placeholder="Invoice No"
                                                value={invoiceNo}
                                                disabled={true}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setInvoiceNo(value);
                                                    if (uploadedFile || !existingInvoice.url) {
                                                        setFieldErrors((prev) => ({
                                                            ...prev,
                                                            invoiceNo: !value.trim(),
                                                        }));
                                                    }
                                                }}
                                            />
                                        </div>
                                        {(uploadedFile || !existingInvoice.url) && fieldErrors.invoiceNo && (
                                            <div className="text-danger mt-1" style={{ fontSize: "14px" }}>
                                                Invoice No is required.
                                            </div>
                                        )}

                                        {/* Invoice Date */}
                                        <div className="d-flex align-items-center mb-3">
                                            <label className="fw-semibold me-2" style={{ width: "200px" }}>Invoice Date</label>
                                            <input
                                                type="date"
                                                disabled={true}
                                                className={`form-control border-dark ${(uploadedFile || !existingInvoice.url) &&
                                                    fieldErrors.invoiceDate ? "is-invalid" : ""}`}
                                                value={invoiceDate}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setInvoiceDate(value);
                                                    if (uploadedFile || !existingInvoice.url) {
                                                        setFieldErrors((prev) => ({
                                                            ...prev,
                                                            invoiceDate:
                                                                !value.trim() ||
                                                                value > new Date().toISOString().split("T")[0],
                                                        }));
                                                    }
                                                }}
                                                max={new Date().toISOString().split("T")[0]}
                                            />
                                        </div>
                                        {(uploadedFile || !existingInvoice.url) && fieldErrors.invoiceDate && (
                                            <div className="text-danger mt-1" style={{ fontSize: "14px" }}>
                                                Invoice Date is required and cannot be future.
                                            </div>
                                        )}

                                        {/* Invoice Amount */}
                                        <div className="d-flex align-items-center mb-3">
                                            <label className="fw-semibold me-2" style={{ width: "200px" }}>Invoice Amount</label>
                                            <input
                                                type="text"
                                                className={`form-control border-dark ${(uploadedFile || !existingInvoice.url) &&
                                                    (fieldErrors.invoiceAmount || amountError) ? "is-invalid" : ""}`}
                                                disabled={true}
                                                placeholder="Enter Amount"
                                                value={invoiceAmount}
                                                onChange={(e) => {
                                                    const enteredAmount = e.target.value;
                                                    setInvoiceAmount(enteredAmount);
                                                    if (uploadedFile || !existingInvoice.url) {
                                                        const parsedAmount = parseFloat(enteredAmount);
                                                        setFieldErrors((prev) => ({
                                                            ...prev,
                                                            invoiceAmount:
                                                                !enteredAmount.trim() ||
                                                                isNaN(parsedAmount) ||
                                                                parsedAmount <= 0,
                                                        }));
                                                        if (
                                                            enteredAmount.trim() &&
                                                            !isNaN(parsedAmount) &&
                                                            parsedAmount > 0
                                                        ) {
                                                            if (Math.abs(parsedAmount - parseFloat(finalAmount)) > 0.01) {
                                                                setAmountError("Invoice Amount must match Total Amount.");
                                                            } else {
                                                                setAmountError("");
                                                            }
                                                        } else {
                                                            setAmountError("");
                                                        }
                                                    }
                                                }}
                                            />
                                        </div>
                                        {(uploadedFile || !existingInvoice.url) && fieldErrors.invoiceAmount && (
                                            <div className="text-danger mt-1" style={{ fontSize: "14px" }}>
                                                Invoice Amount must be a valid positive number.
                                            </div>
                                        )}
                                        {(uploadedFile || !existingInvoice.url) && amountError && (
                                            <div className="text-danger mt-1" style={{ fontSize: "14px" }}>
                                                {amountError}
                                            </div>
                                        )}
                                    </div>
                                </form>
                            </div>
                        </div>

                        <div className="d-flex mt-4 justify-content-center align-items-center flex-wrap gap-3">
                            <Button
                                onClick={downloadCSV}
                                className="d-flex align-items-center"
                                style={{
                                    backgroundColor: "rgb(248, 238, 255)",
                                    border: "1px solid #8000d7",
                                    padding: "10px 50px",
                                    borderRadius: "8px",
                                    fontWeight: "500",
                                    fontSize: "16px",
                                    color: "#8000d7",
                                }}
                            >
                                <FaDownload />
                                <span className="ms-2">Download</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default ApprovedBatchData;
